import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { WishlistTemplateToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useWishlistTemplateMutations } from '~/hooks/use-wishlist-templates';
import { productsApi } from '~/lib/api/client';
import {
	wishlistTemplateSchema,
	EMPTY_WISHLIST_TEMPLATE,
	tokenToForm,
	type WishlistTemplateFormValues
} from '~/lib/schemas/wishlist-template';
import { ProductMultiSelect, type SelectedProduct } from './product-multi-select';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** The raw row token (carries `ProductIds`; `Products` labels are resolved on open). */
	initial: WishlistTemplateToken | null;
}

function selectedFromToken(token: WishlistTemplateToken | null): SelectedProduct[] {
	return (token?.Products ?? []).filter(p => p.id != null).map(p => ({ id: p.id!, nm: p.nm ?? '', cd: p.cd, img1: p.img1 }));
}

/** Add / edit a wishlist template (Πρότυπο Wishlist). */
export function WishlistTemplateEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useWishlistTemplateMutations();
	const [selected, setSelected] = useState<SelectedProduct[]>([]);
	const form = useForm<WishlistTemplateFormValues>({
		resolver: zodResolver(wishlistTemplateSchema) as Resolver<WishlistTemplateFormValues>,
		defaultValues: EMPTY_WISHLIST_TEMPLATE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (!open) return;
		reset(initial ? tokenToForm(initial) : EMPTY_WISHLIST_TEMPLATE);
		// The list endpoint returns ProductIds but not hydrated Products; resolve the chip labels
		// by id (parity with the legacy ProductSelect, which seeds via products getByIds).
		const seeded = selectedFromToken(initial);
		setSelected(seeded);
		const ids = initial?.ProductIds ?? [];
		if (ids.length && seeded.length < ids.length) {
			let cancelled = false;
			productsApi
				.getByIdsAdmin(ids)
				.then(res => {
					if (cancelled) return;
					// Chips follow the template's order (the storefront shows and adopts that order), not the lookup's.
					const byId = new Map((res.data.items ?? []).filter(p => p.id != null).map(p => [p.id!, p]));
					setSelected(
						ids.flatMap(id => {
							const p = byId.get(id);
							return p ? [{ id, nm: p.nm ?? '', cd: p.cd, img1: p.img1 }] : [];
						})
					);
				})
				.catch(() => {});
			return () => {
				cancelled = true;
			};
		}
	}, [open, initial, reset]);

	const onSelectionChange = (next: SelectedProduct[]) => {
		setSelected(next);
		setValue(
			'productIds',
			next.map(p => p.id),
			{ shouldDirty: true }
		);
	};

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{isEdit ? t('catalog.wishlistTemplates.editTitle') : t('catalog.wishlistTemplates.addTitle')}</DialogTitle>
					<DialogDescription>{t('catalog.wishlistTemplates.formSubtitle')}</DialogDescription>
				</DialogHeader>
				{/* min-w-0: a long product name in the search results must not widen the dialog's grid column (BW-8477). */}
				<form onSubmit={onSubmit} noValidate className="flex min-w-0 flex-col gap-4">
					<FormField label={t('catalog.wishlistTemplates.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} maxLength={200} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<FormField label={t('catalog.wishlistTemplates.columns.code')} error={formState.errors.cd?.message}>
						<Input {...register('cd')} maxLength={200} aria-invalid={Boolean(formState.errors.cd)} />
					</FormField>
					<div className="grid grid-cols-2 gap-3">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="wt-iv">{t('catalog.wishlistTemplates.columns.visible')}</Label>
							<Switch id="wt-iv" checked={watch('iv')} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
						</div>
						<FormField label={t('catalog.wishlistTemplates.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<FormField label={t('catalog.wishlistTemplates.columns.products')}>
						<ProductMultiSelect selected={selected} onChange={onSelectionChange} />
					</FormField>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
